import { APP_BASE_HREF } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { NgModule, inject, provideAppInitializer } from '@angular/core'
import { Router } from '@angular/router'

import * as Sentry from '@sentry/angular'
import {
  AuthConfig,
  OAuthModule,
  OAuthModuleConfig,
  OAuthService,
  OAuthStorage,
} from 'angular-oauth2-oidc'

import { catchError, from, switchMap, tap, throwError } from 'rxjs'

import { environment } from '@flowx/designer/environment'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { AuthGuardWithForcedLogin } from './auth-guard-with-forced-login.service'
import { authConfigFactory } from './auth.config'
import { FX_ORGANIZATION_CODE, FX_ORGANIZATION_CODE_TOKEN } from './auth.constants'
import { AuthGuard } from './auth.guard.service'
import { AuthConfigService } from './auth.service'
import { InstallOptions, InstallStatus } from './auth.types'
import { FX_ENVIRONMENT_DEFINITION } from './environment-definition.constants'
import { OnPremInstallGuard } from './on-prem-install-guard'
import { SaasInstallGuard } from './saas-install-guard'
import { attachesAccessToken } from './utils/access-token-urls'

import { AnalyticsService } from '../analytics/analytics.service'

// We need a factory since localStorage is not available at AOT build time
export function storageFactory(): OAuthStorage {
  return localStorage
}

function authModuleConfigFactory(): OAuthModuleConfig {
  return {
    resourceServer: {
      customUrlValidation: (url) => attachesAccessToken(url, environment.baseUrl),
      sendAccessToken: true,
    },
  }
}
function authAppSaasInitializerFactory(
  authService: AuthConfigService,
  authConfig: AuthConfig,
  oAuthService: OAuthService,
  organizationCode: string
): () => Promise<void> {
  return () =>
    authService.getOrganizationAuthConfig(authConfig, organizationCode).then((newAuthConfig) => {
      oAuthService.configure(newAuthConfig)
      Sentry.setTag('instance', newAuthConfig.redirectUri)
      return authService.runInitialLoginSequence()
    })
}

function authAppOnPremInitializerFactory(
  authService: AuthConfigService,
  oAuthService: OAuthService,
  authConfig: AuthConfig
): () => Promise<void> {
  oAuthService.configure(authConfig)
  Sentry.setTag('instance', authConfig.redirectUri)
  return () => authService.runInitialLoginSequence()
}

function extractFromSubdomain(): string | null {
  // TODO this needs to be set on runtime for saas deplymnets as a env variable
  const rootDomain = environment.rootDomain // e.g., saas.dev3.flowxai.dev, cloud.pr1887.dev3.flowxai.dev
  const host = window.location.hostname // e.g., org.saas.dev3.flowxai.dev
  if (host === rootDomain || host.endsWith(`.${rootDomain}`) === false) {
    return null
  }
  return host.replace(`.${rootDomain}`, '') // e.g., org
}

function initSentry(environmentName: string, dsn: string): void {
  Sentry.init({
    dsn,
    integrations: [Sentry.browserTracingIntegration()],
    environment: environmentName,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })

  if (!environment.production) {
    const globalWindow = window as Window & { __triggerSentryTest?: () => void }
    globalWindow.__triggerSentryTest = () => {
      Sentry.captureException(new Error('Sentry smoke test from local development'))
    }
  }
}

@NgModule({
  imports: [OAuthModule.forRoot()],
  providers: [
    AuthConfigService,
    AuthGuard,
    AuthGuardWithForcedLogin,
    SaasInstallGuard,
    OnPremInstallGuard,
    provideAppInitializer(() => {
      const environmentInfo = inject(FX_ENVIRONMENT_DEFINITION)
      const organizationCode = inject(FX_ORGANIZATION_CODE_TOKEN)
      const analyticsService = inject(AnalyticsService)
      const authConfig = inject(AuthConfig)
      const oAuthService = inject(OAuthService)
      const authConfigService = inject(AuthConfigService)
      const router = inject(Router)
      const baseHref = inject(APP_BASE_HREF)
      const sentryDsn = inject<string>('SENTRY_DSN' as any)
      const toastMessageService = inject(ToastMessageService)

      return environmentInfo.pipe(
        tap((env) => {
          analyticsService.init()
          if (sentryDsn) {
            initSentry(env.envName, sentryDsn)
          }
        }),
        switchMap((env) => {
          if (env.installation === InstallOptions.SAAS) {
            if (!organizationCode) {
              return from(Promise.resolve())
            }
            return from(
              authAppSaasInitializerFactory(
                authConfigService,
                authConfig,
                oAuthService,
                organizationCode
              )()
            )
          } else if (env.installation === InstallOptions.ON_PREM) {
            const installationIsCompleteAndOrganizationInfoExists =
              env.status === InstallStatus.OK && env.organizationInfo

            if (installationIsCompleteAndOrganizationInfoExists) {
              const redirectUri = environment.production
                ? env.organizationInfo.redirectUri
                : environment.keycloak.redirectUri

              return from(
                authAppOnPremInitializerFactory(authConfigService, oAuthService, {
                  ...authConfig,
                  ...env.organizationInfo,
                  redirectUri: `${redirectUri}${baseHref}`,
                })()
              )
            } else {
              if (env.status === InstallStatus.MISSING_LICENSE) {
                return from(router.navigate(['start', 'install', 'license']))
              } else if (env.status === InstallStatus.MISSING_ORGANIZATION) {
                return from(router.navigate(['start', 'install', 'organization']))
              } else {
                return throwError(
                  () => new Error('Installation status not supported. Please contact support')
                )
              }
            }
          } else {
            return throwError(
              () => new Error('Installation is not supported. Please contact support')
            )
          }
        }),
        catchError((error) => {
          toastMessageService.showErrorMessage(
            error?.error?.title ||
              (error instanceof HttpErrorResponse ? 'Something went wrong' : error.message)
          )
          return throwError(() => error)
        })
      )
    }),
    {
      provide: FX_ORGANIZATION_CODE_TOKEN,
      useFactory: () =>
        new URL(window.location.href).searchParams.get(FX_ORGANIZATION_CODE) ||
        extractFromSubdomain() ||
        environment.keycloak.defaultOrganizationName ||
        '',
    },
    { provide: AuthConfig, useFactory: authConfigFactory, deps: [FX_ORGANIZATION_CODE_TOKEN] },
    { provide: OAuthModuleConfig, useFactory: authModuleConfigFactory },
    { provide: OAuthStorage, useFactory: storageFactory },
  ],
})
export class AuthConfigModule {
  constructor() {
    const parentModule = inject(AuthConfigModule, { optional: true, skipSelf: true })

    if (parentModule) {
      throw new Error('AuthModule is already loaded. Import it in the AppModule only')
    }
  }
}
